import { BarChart3, PlayCircle, Timer, Trophy } from "lucide-react";
import { motion, useInView } from "motion/react";
import { useRef } from "react";
import { Badge } from "@/components/ui/badge";

const features = [
  {
    icon: Timer,
    title: "Weekly iRacing Setups",
    description:
      "Fresh iRacing setups for every track, every week. Stay competitive all season long.",
    premium: false,
  },
  {
    icon: PlayCircle,
    title: "Pro Replay Files",
    description:
      "Watch exactly how our drivers nail every corner. Learn the fast line from the best.",
    premium: true,
  },
  {
    icon: BarChart3,
    title: "Telemetry Data",
    description:
      "Compare your driving inputs against the pros. See where you're leaving time on the table.",
    premium: true,
  },
  {
    icon: Trophy,
    title: "Every iRacing Series",
    description: "From MX-5 rookies to IMSA GT3 endurance. Every official iRacing series covered.",
    premium: false,
  },
];

export function FeaturesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="relative py-24 px-4" ref={ref}>
      {/* Subtle Kalocsai pattern behind section title */}
      <img
        src="/kalocsai-corner-tl.webp"
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute top-8 left-8 w-64 h-64 object-contain"
        style={{
          opacity: 0.05,
          maskImage: "radial-gradient(ellipse at top left, black 20%, transparent 65%)",
          WebkitMaskImage: "radial-gradient(ellipse at top left, black 20%, transparent 65%)",
        }}
      />
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <h2 className="font-display text-4xl font-bold uppercase tracking-tight text-foreground md:text-5xl">
            Everything You Need
            <br />
            <span className="text-primary">to Go Faster</span>
          </h2>
        </motion.div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                className="group relative rounded-xl border border-border bg-card/50 p-6 backdrop-blur-sm transition-all duration-300 hover:border-folk-blue/30 hover:bg-card"
              >
                {feature.premium && (
                  <Badge className="absolute -top-2.5 right-4 bg-folk-red text-white hover:bg-folk-red/90 z-10">
                    Premium
                  </Badge>
                )}
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-folk-blue/10 text-folk-blue transition-colors group-hover:bg-folk-blue/20">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-foreground">{feature.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
